/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new QuantitySelectorUpdateEvent(quantity, cartLine));
 * document.addEventListener(ThemeEvents.quantitySelectorUpdate, (e) => { console.log(e.detail.quantity) });
 */
export class ThemeEvents {
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  static mediaStartedPlaying = 'media:started-playing';
  // Event triggered when quantity-selector value is changed
  static quantitySelectorUpdate = 'quantity-selector:update';
  /** @static @constant {string} Event triggered when a predictive search is expanded */
  static megaMenuHover = 'megaMenu:hover';
  /** @static @constant {string} Event triggered when a zoom dialog media is selected */
  static zoomMediaSelected = 'zoom-media:selected';
  /** @static @constant {string} Event triggered when a product's favorited state changes */
  static favoritesChanged = 'favorites:changed';
  /** @static @constant {string} Event triggered when a product is added to favorites, carrying display details for UI feedback */
  static favoriteAdded = 'favorites:added';
  /** @static @constant {string} Event triggered when a product is removed from favorites, carrying display details for UI feedback */
  static favoriteRemoved = 'favorites:removed';
  /** @static @constant {string} Event triggered when the recently-viewed products list changes (add, remove or restore) */
  static recentlyViewedChanged = 'recently-viewed:changed';
  /** @static @constant {string} Event triggered when a product is removed from recently viewed, carrying display details for UI feedback */
  static recentlyViewedRemoved = 'recently-viewed:removed';
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class QuantitySelectorUpdateEvent extends Event {
  /**
   * Creates a new QuantitySelectorUpdateEvent
   * @param {number} quantity - Quantity value
   * @param {number} [cartLine] - The id of the updated cart line
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
    this.detail = {
      quantity,
      cartLine,
    };
  }
}

/**
 * Event class for media playback starts
 * @extends {Event}
 */
export class MediaStartedPlayingEvent extends Event {
  /**
   * Creates a new MediaStartedPlayingEvent
   * @param {HTMLElement} resource - The element containing the video that emitted the event
   */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * @typedef {Object} SlideshowSelectEventData
 * @property {number} index
 * @property {string | null} id
 * @property {Element} slide
 * @property {number} previousIndex
 * @property {boolean} userInitiated
 * @property {'select' | 'scroll' | 'drag'} trigger
 */

export class SlideshowSelectEvent extends Event {
  /**  @param {SlideshowSelectEventData} data */
  constructor(data) {
    super(SlideshowSelectEvent.eventName, { bubbles: true });
    this.detail = data;
  }

  /** @type {SlideshowSelectEventData} */
  detail;

  static eventName = 'slideshow:select';
}

/**
 * Event class for zoom dialog media selection
 * @extends {Event}
 */
export class ZoomMediaSelectedEvent extends Event {
  /**
   * Creates a new ZoomMediaSelectedEvent
   * @param {number} index - The index of the selected media
   */
  constructor(index) {
    super(ThemeEvents.zoomMediaSelected, { bubbles: true });
    this.detail = {
      index,
    };
  }
}

/**
 * Event class for mega menu hover being hovered over
 * @extends {Event}
 */
export class MegaMenuHoverEvent extends Event {
  constructor() {
    super(ThemeEvents.megaMenuHover, { bubbles: true });
  }
}

/**
 * Event class for favorited state changes
 * @extends {Event}
 */
export class FavoritesChangedEvent extends Event {
  /**
   * Creates a new FavoritesChangedEvent
   * @param {string} handle - The product handle whose favorited state changed
   * @param {boolean} isFavorite - Whether the product is now favorited
   */
  constructor(handle, isFavorite) {
    super(ThemeEvents.favoritesChanged, { bubbles: true });
    this.detail = {
      handle,
      isFavorite,
    };
  }
}

/**
 * Event class for a product being added to favorites, carrying the details needed to render UI feedback (e.g. a toast)
 * @extends {Event}
 */
export class FavoriteAddedEvent extends Event {
  /**
   * Creates a new FavoriteAddedEvent
   * @param {object} detail
   * @param {string} detail.handle - The product handle that was favorited
   * @param {string} detail.title - The product title
   * @param {string} detail.image - The product image URL
   */
  constructor(detail) {
    super(ThemeEvents.favoriteAdded, { bubbles: true });
    this.detail = detail;
  }
}

/**
 * Event class for a product being removed from favorites, carrying the details needed to render UI feedback (e.g. an undo toast)
 * @extends {Event}
 */
export class FavoriteRemovedEvent extends Event {
  /**
   * Creates a new FavoriteRemovedEvent
   * @param {object} detail
   * @param {string} detail.handle - The product handle that was unfavorited
   * @param {string} detail.title - The product title
   * @param {string} detail.image - The product image URL
   * @param {number} detail.index - The handle's index in the favorites list just before removal, so undo can restore it there
   */
  constructor(detail) {
    super(ThemeEvents.favoriteRemoved, { bubbles: true });
    this.detail = detail;
  }
}

/**
 * Event class for recently-viewed list changes (add, remove or restore via undo)
 * @extends {Event}
 */
export class RecentlyViewedChangedEvent extends Event {
  /**
   * Creates a new RecentlyViewedChangedEvent
   * @param {string} productId - The product id whose presence in the list changed
   * @param {boolean} isPresent - Whether the product is now in the recently-viewed list
   */
  constructor(productId, isPresent) {
    super(ThemeEvents.recentlyViewedChanged, { bubbles: true });
    this.detail = {
      productId,
      isPresent,
    };
  }
}

/**
 * Event class for a product being removed from recently viewed, carrying the details needed to render UI feedback (e.g. an undo toast)
 * @extends {Event}
 */
export class RecentlyViewedRemovedEvent extends Event {
  /**
   * Creates a new RecentlyViewedRemovedEvent
   * @param {object} detail
   * @param {string} detail.productId - The product id that was removed
   * @param {string} detail.title - The product title
   * @param {string} detail.image - The product image URL
   * @param {number} detail.index - The product's index in the recently-viewed list just before
   * removal, so undo can restore it there
   */
  constructor(detail) {
    super(ThemeEvents.recentlyViewedRemoved, { bubbles: true });
    this.detail = detail;
  }
}
